/**
 * Shared coordination mechanism for deep link processing
 * Prevents duplicate processing when multiple components (DailyAlertsCard, OfficeNotesCard)
 * are processing the same alertId from URL query params
 */

// Module-level refs to track processing state across all component instances
const processedAlertIds = new Set<string>();
const processingTimeouts = new Map<string, NodeJS.Timeout>();

/**
 * Check if an alertId has already been processed
 */
export const isAlertIdProcessed = (alertId: string): boolean => {
  return processedAlertIds.has(alertId);
};

/**
 * Mark an alertId as processed
 */
export const markAlertIdAsProcessed = (alertId: string): void => {
  processedAlertIds.add(alertId);
};

/**
 * Clear processed state for an alertId (when removed from URL)
 */
export const clearProcessedAlertId = (alertId: string | null): void => {
  if (alertId) {
    processedAlertIds.delete(alertId);
    // Also clear any pending timeout
    const timeout = processingTimeouts.get(alertId);
    if (timeout) {
      clearTimeout(timeout);
      processingTimeouts.delete(alertId);
    }
  }
};

/**
 * Set a timeout for processing an alertId
 * Returns true if timeout was set, false if one already exists
 */
export const setProcessingTimeout = (
  alertId: string,
  callback: () => void,
  delay: number
): boolean => {
  // Clear existing timeout if any
  const existingTimeout = processingTimeouts.get(alertId);
  if (existingTimeout) {
    clearTimeout(existingTimeout);
  }

  const timeout = setTimeout(() => {
    callback();
    processingTimeouts.delete(alertId);
  }, delay);

  processingTimeouts.set(alertId, timeout);
  return true;
};

/**
 * Clear a processing timeout for an alertId
 */
export const clearProcessingTimeout = (alertId: string): void => {
  const timeout = processingTimeouts.get(alertId);
  if (timeout) {
    clearTimeout(timeout);
    processingTimeouts.delete(alertId);
  }
};

/**
 * Clear all processing state (useful for testing or cleanup)
 */
export const clearAllProcessingState = (): void => {
  processedAlertIds.clear();
  processingTimeouts.forEach((timeout) => clearTimeout(timeout));
  processingTimeouts.clear();
};

